import { useCallback, useEffect, useRef } from 'react';
import { usePageExperienceContext } from '@/features/experiences/contexts/PageExperienceContext';
import { useUpdateExperience } from '@/features/experiences/hooks/useUpdateExperience';
import type { PageExperience } from '@/features/experiences/types';
import type { UpdateExperienceInput } from '@/types/gql.generated';

/**
 * Which experience a control edits: 'child' for the Views panel (the active
 * view), 'experience' for the Style panel (the whole schedule). An experience
 * with no children is its own view, so both land on it.
 */
export type PreviewScope = 'child' | 'experience';

// Not narrowed to `keyof UpdateExperienceInput` as well: that resolves to plain
// `string` (the generated input carries an index signature), so intersecting
// buys no safety and collapses the field literal, which breaks inference on
// every binding.
type Field = keyof PageExperience;

/** Bindings for a control offering several values (select, segmented, radio). */
export type ValueBinding<V> = {
  value: V;
  onChange: (value: V) => void;
  /** The hovered option, or null once nothing is hovered. */
  onPreview: (value: V | null) => void;
};

/** Bindings for a single-element control whose only value is its inversion. */
export type ToggleBinding = {
  isChecked: boolean;
  onChange: () => void;
};

/**
 * Two call signatures rather than one with `V = PageExperience[F]` defaulted:
 * the compiler resolves that default without the inferred `F`, collapsing every
 * untransformed binding's value type.
 */
type Register = {
  <F extends Field>(
    scope: PreviewScope,
    field: F
  ): ValueBinding<PageExperience[F]>;
  <F extends Field, V>(
    scope: PreviewScope,
    field: F,
    options: RegisterOptions<F, V>
  ): ValueBinding<V>;
};

/**
 * For a toggle over a field that isn't a plain boolean — "shown" is an enum
 * whose value comes from the layout's options, so both directions need a map.
 */
type ToggleOptions<F extends Field> = {
  toChecked: (value: PageExperience[F]) => boolean;
  toField: (checked: boolean) => PageExperience[F];
};

type RegisterOptions<F extends Field, V> = {
  /** Maps the control's value onto the field. Identity when omitted. */
  toField?: (value: V) => PageExperience[F];
  /**
   * Maps the field's value onto the control. Identity when omitted. Also given
   * the saved experience, for a field whose display value depends on its
   * siblings — a null week start reads its default off the layout.
   */
  toControl?: (value: PageExperience[F], saved: PageExperience) => V;
  /**
   * Replaces the default persist, for a field whose write isn't a plain
   * assignment — one that also refetches events, or is derived from other
   * state. The preview is unaffected; only the click changes.
   */
  commit?: (value: V) => void;
};

/**
 * Binds a customize control to one field of the experience, in the manner of
 * react-hook-form's `register`: spread the result onto the control and it
 * reports the saved value, previews the hovered one, and persists a click.
 * Only a control offering several values previews on hover; see
 * `registerToggle`.
 *
 *     <SidebarRowSwitch {...registerToggle('child', 'eventCardEmojiEnabled')} />
 *     <SidebarRowSelect {...register('child', 'timeFormat')} options={OPTIONS} />
 *
 * Routing controls through here puts the two rules each one has to get right —
 * which experience holds the SAVED value for its scope, and which experience a
 * write targets — in a single place instead of at every call site. Getting the
 * first wrong is not a visible mistake: the control just follows its own hover,
 * which reads as a click that already happened.
 */
export const useCustomizeControl = () => {
  const {
    experience,
    isChild,
    parentExperience,
    persistedExperience,
    setPreview,
    setParentPreview,
  } = usePageExperienceContext();
  const { updateExperience } = useUpdateExperience();

  const setterFor = useCallback(
    (scope: PreviewScope) =>
      scope === 'experience' ? setParentPreview : setPreview,
    [setPreview, setParentPreview]
  );

  // Where a scope's SAVED value lives.
  //
  // 'experience' — always `persistedExperience`. In child scope the view
  // inherits these fields through ChildExperienceLoader's graft, which carries
  // a live preview along with the committed value (that graft is the route the
  // preview takes to reach the page), so the view is not a safe source.
  //
  // 'child' — the loaded view, which the sidebar's loader keeps clear of
  // previews (applyPreview={false}). With no children there is no separate
  // view and the experience previews itself, so its saved copy serves instead.
  const savedFor = (scope: PreviewScope) =>
    scope === 'experience' || !isChild ? persistedExperience : experience;

  // Every field this component bound, so an unmount mid-hover can release them
  // all: picking an option closes its menu and leaving the panel unmounts the
  // rail, neither of which fires a mouseleave.
  const registered = useRef(new Set<`${PreviewScope}:${Field}`>());

  const clearField = useCallback(
    (scope: PreviewScope, field: Field) => {
      setterFor(scope)((current) => {
        if (!current) {
          return null;
        }
        const next = { ...current };
        delete next[field];
        return Object.keys(next).length > 0 ? next : null;
      });
    },
    [setterFor]
  );

  const applyField = useCallback(
    (scope: PreviewScope, field: Field, value: PageExperience[Field]) => {
      registered.current.add(`${scope}:${field}`);
      setterFor(scope)((current) => ({ ...current, [field]: value }));
    },
    [setterFor]
  );

  const persistField = useCallback(
    (scope: PreviewScope, field: Field, value: PageExperience[Field]) => {
      updateExperience(
        { [field]: value } as Omit<UpdateExperienceInput, 'experienceId'>,
        // An 'experience'-scoped write from inside a child has to name the
        // parent: useUpdateExperience is otherwise scoped to the loaded view.
        scope === 'experience' && isChild
          ? { experienceId: parentExperience?.id }
          : undefined
      );
    },
    [updateExperience, isChild, parentExperience?.id]
  );

  // Cast because an overloaded call signature can't be inferred from a single
  // implementation; the two signatures above are what callers see.
  const register = ((
    scope: PreviewScope,
    field: Field,
    options?: RegisterOptions<Field, unknown>
  ): ValueBinding<unknown> => {
    const { toField, toControl, commit } = options ?? {};
    const savedExperience = savedFor(scope);
    const saved = savedExperience[field];
    const asField = (value: unknown) =>
      toField ? toField(value) : (value as PageExperience[Field]);

    return {
      value: toControl ? toControl(saved, savedExperience) : saved,
      onChange: (value) => {
        // The click commits what was previewed, so release the preview and let
        // the committed value drive the page from here.
        clearField(scope, field);
        if (commit) {
          commit(value);
          return;
        }
        persistField(scope, field, asField(value));
      },
      onPreview: (value) =>
        value === null
          ? clearField(scope, field)
          : applyField(scope, field, asField(value)),
    };
  }) as Register;

  /**
   * Deliberately without the hover preview `register` gives a value control: a
   * switch that renders its own inversion under the pointer reads as a click
   * that already landed, and testers found that confusing on toggles even while
   * they liked it on colors and vibes (AH-2441).
   */
  const registerToggle = <F extends Field>(
    scope: PreviewScope,
    field: F,
    options?: ToggleOptions<F>
  ): ToggleBinding => {
    const { toChecked, toField } = options ?? {};
    const saved = savedFor(scope)[field];
    const isChecked = toChecked ? toChecked(saved) : Boolean(saved);
    // The inversion a click would commit, which for a field that isn't a plain
    // boolean is whatever `toField` says "on" and "off" look like.
    const inverted = (
      toField ? toField(!isChecked) : !isChecked
    ) as PageExperience[Field];

    return {
      isChecked,
      onChange: () => {
        // A layout preview carries several of these same fields, so release
        // this one and let the committed value drive the page from here.
        clearField(scope, field);
        persistField(scope, field, inverted);
      },
    };
  };

  // Synced in an effect rather than during render, which React may discard.
  const releaseAll = () =>
    registered.current.forEach((key) => {
      const [scope, field] = key.split(':') as [PreviewScope, Field];
      clearField(scope, field);
    });
  const releaseAllRef = useRef(releaseAll);
  useEffect(() => {
    releaseAllRef.current = releaseAll;
  });
  useEffect(() => () => releaseAllRef.current(), []);

  // Exposed for controls that bind no value of their own but still have to read
  // from the same copy a binding would, per the rule above.
  return { register, registerToggle, savedFor };
};
